/* eslint-disable max-lines */
import {
  Modal,
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from 'reactstrap';
import ReactDatetime from 'react-datetime';
import moment, { Moment } from 'moment';
import isDirector from '../../../../helpers/isDirector';
import { toast } from 'react-toastify';
import isAdmin from '../../../../helpers/isAdmin';
import confirmAlertHelper from '../../../../helpers/confirmAlert';
import { useContext, useEffect, useState } from 'react';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import PtoService from '../../../../services/Pto';
import isUpperManagement from '../../../../helpers/isUpperManagement';
import EmployeeService from '../../../../services/Employee';
import { createOptionsPage } from '../../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../../entities/PagedMeta';
import LoaderSpinner from '../../../../components/Core/LoaderSpinner';
import AsyncSelect from 'react-select/async';
import isHRUser from '../../../../helpers/isHRUser';

type ModalTimeoffRequestProps = {
  isOpen: boolean;
  currentUser: any;
  toggleModal: (e?: any) => void;
  blackoutDates: any[];
  hollidayDates: any[];
  editPtoRequestData: any;
};

const ModalTimeoffRequest = ({
  isOpen,
  toggleModal,
  currentUser,
  blackoutDates,
  hollidayDates,
  editPtoRequestData,
}: ModalTimeoffRequestProps) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hours, setHours] = useState('');
  const [describe, setDescribe] = useState('');
  const [reason, setReason] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<any>(currentUser.id);
  const [timeRequested, setTimeRequested] = useState('');
  const [ptoId, setPtoId] = useState('');
  const [employees, setEmployees] = useState<any>([]);
  const isAdminUser =
    isAdmin(currentUser) || isDirector(currentUser) || isUpperManagement(currentUser) || isHRUser(currentUser);
  const [loading] = useState(false);
  const policyName = employees.find((employee: any) => employee.userId === selectedEmployeeId)?.ptoPolicy?.name;
  const policyId = employees.find((employee: any) => employee.userId === selectedEmployeeId)?.ptoPolicyId;
  const selectedEmployeeData = employees.find((employee: any) => employee.userId === selectedEmployeeId);

  const { token, currentSchool }: any = useContext(AuthContext);

  const asyncSelectStyles = {
    control: (styles: any) => ({
      ...styles,
      ...{ border: 0 },
    }),
    input: (styles: any) => ({ ...styles, ...{ border: 0 } }),
    placeholder: (styles: any) => ({
      ...styles,
      ...{ fontSize: '0.810rem', fontWeight: 400, lineHeight: 1.5, color: '#8898aa', textOverflow: 'clip' },
    }),
    singleValue: (styles: any) => ({
      ...styles,
      ...{ fontSize: '0.810rem', fontWeight: 400, lineHeight: 1.5, color: '#8898aa', textOverflow: 'clip' },
    }),
  };

  const countBusinessDays = (hoursPerWorkday: number, initial?: any, end?: any) => {
    if (!initial || !end) return 0;

    const startBusinessDate = moment(initial).utc();
    const endBusinessDate = moment(end).utc();
    let counter = 0;
    const currentBusinessDate = startBusinessDate.clone();

    while (currentBusinessDate.isSameOrBefore(endBusinessDate)) {
      if (
        currentBusinessDate.day() !== 0 &&
        currentBusinessDate.day() !== 6 &&
        !hollidayDates.includes(currentBusinessDate.format('YYYY-MM-DD'))
      ) {
        counter++;
      }
      currentBusinessDate.add(1, 'days');
    }

    return counter * hoursPerWorkday;
  };

  const auxHours = countBusinessDays(selectedEmployeeData?.ptoPolicy?.hoursPerWorkday, startDate, endDate);
  let disableSave = false;
  if (
    auxHours > selectedEmployeeData?.ptoPolicy.currentBalance &&
    reason !== 'Day Off Non Paid' &&
    !isAdmin(currentUser) &&
    !isDirector(currentUser) &&
    !isUpperManagement(currentUser)
  ) {
    disableSave = true;
  }

  const formatDay = (day: string) => moment(day, 'YYYY-MM-DD').format('MM/DD/YYYY');

  const findBlockedDates = () => {
    const startDateMoment = moment(startDate).utc();
    const endDateMoment = moment(endDate).utc();
    const blockedDates: string[] = [];
    if (endDateMoment > startDateMoment) {
      let currentDate = moment(startDate).utc();
      while (endDateMoment >= currentDate) {
        const day = currentDate.format('YYYY-MM-DD');
        if (blackoutDates.includes(day)) blockedDates.push(day);
        currentDate = currentDate.add(1, 'day');
      }
    }
    if (endDateMoment.format('YYYY-MM-DD') === startDateMoment.format('YYYY-MM-DD')) {
      const day = startDateMoment.format('YYYY-MM-DD');
      if (blackoutDates.includes(day)) blockedDates.push(day);
    }
    return blockedDates;
  };

  const submitNewTimeOffRequest = async (event: React.FormEvent, confirmation: boolean = false): Promise<any> => {
    event.preventDefault();
    const data = {
      userId: selectedEmployeeId,
      schoolId: currentSchool.value,
      policyId,
      reason,
      startDate: moment(startDate).utc().format('YYYY-MM-DD'),
      endDate: moment(timeRequested === 'Less than one day' ? startDate : endDate)
        .utc()
        .format('YYYY-MM-DD'),
      hours: hours ? Number(hours) : Number(auxHours || 0),
      describe,
      confirmation: confirmation,
    };

    try {
      if (!ptoId) {
        await PtoService(token).timeOffRequestCreate(data);
      } else {
        await PtoService(token).timeOffRequestUpdate(ptoId, data);
      }
      setSelectedEmployeeId('');
      setReason('');
      setStartDate('');
      setEndDate('');
      setHours('');
      setDescribe('');
      toast.success('Saved Successfully!');
    } catch (err: any) {
      if (err?.response?.data?.errors?.code === 'NEED_CONFIRMATION') {
        return confirmAlertHelper({
          title: 'Time Off Request Creation',
          messageType: 'SAVE',
          message: 'This employee is still in their probationary period. Are you sure you wish to use PTO?',
          onClickYes: () => submitNewTimeOffRequest(event, true),
        });
      }
      toast.error(err?.response?.data?.message || 'Failed to save!');
    } finally {
      toggleModal(editPtoRequestData);
    }
  };

  const handleSubmitNewTimeOffRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    const blockedDates = findBlockedDates().map(formatDay).join(', ');
    if (!blockedDates) {
      return submitNewTimeOffRequest(event);
    }
    if (!isAdmin(currentUser) && !isDirector(currentUser) && !isUpperManagement(currentUser)) {
      const requestedStart = moment(startDate).utc().format('MM/DD/YYYY');
      const requestedEnd = moment(endDate).utc().format('MM/DD/YYYY');
      return toast.error(
        'You cannot request a time off for a blackout date. ' +
          `Blackout date: ${blockedDates}. Requested period: ${requestedStart} to ${requestedEnd}.`,
      );
    }
    confirmAlertHelper({
      title: 'Submit',
      messageType: 'SAVE',
      message: `Are you sure you would like to override this PTO request during blackout dates (${blockedDates})?`,
      onClickYes: () => submitNewTimeOffRequest(event),
    });
  };

  const loadEmployeesToSelect = async (search?: string) => {
    try {
      const newFilteredOptions = {
        filters: [] as any[],
        options: createOptionsPage(createPagedMeta({ perPage: 99999999 })),
      };
      const filter = {
        field: 'schoolId',
        operation: 'hash',
        value: currentSchool.value,
      };
      if (!Array.isArray(newFilteredOptions?.filters)) {
        newFilteredOptions.filters = [];
      }
      newFilteredOptions.filters.push(filter);
      if (!isAdminUser) {
        newFilteredOptions.filters.push({
          field: 'userId',
          operation: 'hash',
          value: currentUser.id,
        });
      }
      if (search) {
        newFilteredOptions.filters.push({
          operation: 'or',
          filters: [
            {
              field: 'user.firstName',
              operation: 'ilike',
              value: `%${search}%`,
            },
            {
              field: 'user.lastName',
              operation: 'ilike',
              value: `%${search}%`,
            },
          ],
        });
      }
      if (isDirector(currentUser)) {
        newFilteredOptions.filters.push({
          operation: 'or',
          filters: [
            {
              field: 'supervisorId',
              operation: 'hash',
              value: currentUser?.id,
            },
            {
              field: 'userId',
              operation: 'hash',
              value: currentUser?.id,
            },
          ],
        });
      }

      newFilteredOptions.filters.push({
        field: 'status',
        operation: '=',
        value: 'ACTIVE',
      });

      const result = await EmployeeService(token || '').list(newFilteredOptions);
      const dataResult = result?.data || [];
      const data = isDirector(currentUser)
        ? dataResult?.filter((d: any) => d?.supervisorId === currentUser?.id || d?.userId === currentUser?.id)
        : dataResult;

      setEmployees(data);
      return data.map((d: any) => ({ label: `${d.user.firstName} ${d.user.lastName}`, value: d.userId }));
    } catch (e: any) {
      return [];
    }
  };

  const verifyEndDate = (current: Moment) => {
    const startDateMoment = moment(startDate).utc();
    return current.isAfter(startDateMoment);
  };

  const verifyStartDate = (current: Moment) => {
    if (isAdmin(currentUser)) return true;
    const isForSelf = !selectedEmployeeId || Number(selectedEmployeeId) === Number(currentUser.id);
    if (!isForSelf) return true;
    return current.isSameOrBefore(moment().add(6, 'months').endOf('day'));
  };

  useEffect(() => {
    if (!isOpen) return;
    setSelectedEmployeeId(isAdminUser ? '' : currentUser.id);
    setReason('');
    setStartDate('');
    setEndDate('');
    setHours('');
    setDescribe('');
    setTimeRequested('');
  }, [isOpen]);

  useEffect(() => {
    if (editPtoRequestData) {
      setPtoId(editPtoRequestData.ptoId);
      setSelectedEmployeeId(editPtoRequestData.selectedEmployeeId);
      setReason(editPtoRequestData.reason);
      setStartDate(editPtoRequestData.startDate);
      setEndDate(editPtoRequestData.endDate);
      setHours(editPtoRequestData.hours);
      setDescribe(editPtoRequestData.describe);
      setTimeRequested(editPtoRequestData.timeRequested);
    }
  }, [editPtoRequestData]);

  useEffect(() => {
    if (!startDate || timeRequested !== 'One or more Days') return;
    setEndDate(moment(startDate).add(1, 'day').format('MM/DD/YYYY'));
  }, [startDate]);

  return (
    <Modal className="modal-dialog-centered" isOpen={isOpen} toggle={() => toggleModal(editPtoRequestData)} size={'md'}>
      {loading ? (
        <Row>
          <Col style={{ minHeight: '100px' }}>
            <LoaderSpinner />
          </Col>
        </Row>
      ) : (
        <>
          <div className="modal-header">
            <h4 className="modal-title">Record a new Time Off Request</h4>
            <div onClick={() => toggleModal(editPtoRequestData)} className="d-flex p-1 cursor-pointer">
              <i className="fas fa-times" />
            </div>
          </div>
          <div className="modal-body">
            <Form onSubmit={handleSubmitNewTimeOffRequest}>
              <Row style={{ borderBottom: '1px solid #dddddd' }}>
                <Col lg="12" xs="12">
                  {!selectedEmployeeData && (
                    <FormGroup>
                      <Label className="form-control-label">Name</Label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={loadEmployeesToSelect}
                        styles={asyncSelectStyles}
                        placeholder={''}
                        value={selectedEmployeeId}
                        onChange={(e) => setSelectedEmployeeId(e.value)}
                      />
                    </FormGroup>
                  )}

                  {selectedEmployeeData && (
                    <FormGroup>
                      <Label className="form-control-label">Name:</Label>
                      <div className="d-flex align-items-center">
                        <div
                          style={{
                            width: '35px',
                            height: '35px',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={selectedEmployeeData.user.picture}
                            alt="User Profile"
                            className="img-fluid"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </div>
                        <span className="ml-2">{`${selectedEmployeeData?.user?.firstName || '-'} ${
                          selectedEmployeeData?.user?.lastName || '-'
                        }`}</span>
                        {isAdminUser && (
                          <Button
                            style={{
                              border: 'none',
                              boxShadow: 'none',
                              background: '#FFFFFF',
                              padding: 0,
                              marginLeft: 30,
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              setSelectedEmployeeId('');
                            }}
                          >
                            <i className="fas fa-times" />
                          </Button>
                        )}
                      </div>
                    </FormGroup>
                  )}
                </Col>

                <Col lg="12" xs="12">
                  <FormGroup>
                    <Label className="form-control-label">PTO Policy:</Label>
                    <br />
                    <span>{policyName ? policyName : '-'}</span>
                  </FormGroup>
                </Col>
                <Col lg="12" xs="12">
                  <FormGroup>
                    <Label className="form-control-label">Reason</Label>
                    <Input type="select" value={reason} onChange={(event) => setReason(event.target.value)} required>
                      <option value=""></option>
                      <option value="VIP/Vacation/Sick">VIP/Vacation/Sick</option>
                      <option value="Jury Duty">Jury Duty</option>
                      <option value="Bereavement">Bereavement</option>
                      <option value="Day Off Non Paid">Day Off Non Paid</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <Row className="mt-4">
                <Col lg="12">
                  <Label>How much time are you requesting?</Label>
                  <Row>
                    <Col lg="6" xs="6">
                      <Button
                        style={{
                          border: timeRequested === 'One or more Days' ? '1px solid #5ca8ff' : '1px solid #000000',
                          color: timeRequested === 'One or more Days' ? '#5ca8ff' : '#000000',
                          fontSize: '12px',
                          width: '155px',
                          height: '75px',
                        }}
                        size="sm"
                        outline={timeRequested !== 'One or more Days'}
                        onClick={() => {
                          setStartDate(editPtoRequestData?.startDate);
                          setEndDate(
                            editPtoRequestData?.startDate
                              ? moment(editPtoRequestData?.startDate).add(1, 'day').format('MM/DD/YYYY')
                              : '',
                          );
                          if (timeRequested !== 'One or more Days') {
                            setTimeRequested('One or more Days');
                          } else {
                            setTimeRequested('');
                          }
                        }}
                      >
                        More Than One Day
                      </Button>
                    </Col>
                    <Col lg="6" xs="6">
                      <Button
                        style={{
                          border: timeRequested === 'Less than one day' ? '1px solid #5ca8ff' : '1px solid #000000',
                          color: timeRequested === 'Less than one day' ? '#5ca8ff' : '#000000',
                          fontSize: '12px',
                          width: '155px',
                          height: '75px',
                        }}
                        size="sm"
                        outline={timeRequested !== 'Less than one day'}
                        onClick={() => {
                          setStartDate(editPtoRequestData?.startDate);
                          setEndDate(editPtoRequestData?.endDate);
                          if (timeRequested !== 'Less than one day') {
                            setTimeRequested('Less than one day');
                          } else {
                            setTimeRequested('');
                          }
                        }}
                      >
                        One Day or Less
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
              {timeRequested === 'One or more Days' && (
                <>
                  <Row className="mt-4">
                    <Col lg="4">
                      <FormGroup>
                        <label className="form-control-label">Start Date</label>
                        <InputGroup className="input-group-alternative">
                          <ReactDatetime
                            inputProps={{
                              placeholder: 'Start Date',
                            }}
                            onChange={(newValue) => {
                              setStartDate(`${newValue}`);
                            }}
                            timeFormat={false}
                            value={startDate ? `${moment(startDate).utc().format('MM/DD/yyyy')}` : ''}
                            dateFormat={'MM/DD/yyyy'}
                            closeOnSelect={true}
                            isValidDate={verifyStartDate}
                          />
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <i className="ni ni-calendar-grid-58" />
                            </InputGroupText>
                          </InputGroupAddon>
                        </InputGroup>
                      </FormGroup>
                    </Col>

                    <Col lg="4">
                      <FormGroup>
                        <label className="form-control-label">End Date</label>
                        <InputGroup className="input-group-alternative">
                          <ReactDatetime
                            inputProps={{
                              placeholder: 'End Date',
                            }}
                            onChange={(newValue) => {
                              setEndDate(`${newValue}`);
                            }}
                            timeFormat={false}
                            value={endDate ? `${moment(endDate).utc().format('MM/DD/yyyy')}` : ''}
                            dateFormat={'MM/DD/yyyy'}
                            initialValue={startDate}
                            closeOnSelect={true}
                            isValidDate={verifyEndDate}
                          />
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <i className="ni ni-calendar-grid-58" />
                            </InputGroupText>
                          </InputGroupAddon>
                        </InputGroup>
                      </FormGroup>
                    </Col>

                    <Col lg="4">
                      <FormGroup>
                        <label className="form-control-label">Hours</label>
                        <Input type="text" value={auxHours} disabled />
                        {!isUpperManagement(currentUser) &&
                        !isAdmin(currentUser) &&
                        !isDirector(currentUser) &&
                        reason !== 'Day Off Non Paid' ? (
                          <>
                            {Number(auxHours) >
                              Number(selectedEmployeeData?.ptoPolicy?.currentBalance) -
                                Number(selectedEmployeeData?.ptoPolicy?.scheduleHours) && (
                              <label className="text-sm text-danger">
                                You cannot request more hours than your current limit of{' '}
                                {Number(selectedEmployeeData?.ptoPolicy?.currentBalance) -
                                  Number(selectedEmployeeData?.ptoPolicy?.scheduleHours) >
                                0
                                  ? Number(selectedEmployeeData?.ptoPolicy?.currentBalance) -
                                    Number(selectedEmployeeData?.ptoPolicy?.scheduleHours)
                                  : 0}
                                .{' Please change reason to "Day Off Non Paid".'}
                              </label>
                            )}
                          </>
                        ) : (
                          <></>
                        )}
                      </FormGroup>
                    </Col>

                    <Col lg="12">
                      <FormGroup>
                        <label className="form-control-label">Why are you taking off?</label>
                        <Input type="text" value={describe} onChange={(event) => setDescribe(event.target.value)} />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="d-flex align-items-center justify-content-end pr-3">
                    {policyName && (
                      <Button color="warning" disabled={disableSave}>
                        Save
                      </Button>
                    )}
                  </Row>
                </>
              )}

              {timeRequested === 'Less than one day' && (
                <>
                  <Row className="mt-4">
                    <Col lg="6">
                      <FormGroup>
                        <label className="form-control-label">Date</label>
                        <InputGroup className="input-group-alternative">
                          <ReactDatetime
                            inputProps={{
                              placeholder: 'Date',
                            }}
                            onChange={(newValue) => {
                              setStartDate(`${newValue}`);
                              setEndDate(`${newValue}`);
                            }}
                            timeFormat={false}
                            value={startDate ? `${moment(startDate).utc().format('MM/DD/yyyy')}` : ''}
                            dateFormat={'MM/DD/yyyy'}
                            closeOnSelect={true}
                            isValidDate={verifyStartDate}
                          />
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <i className="ni ni-calendar-grid-58" />
                            </InputGroupText>
                          </InputGroupAddon>
                        </InputGroup>
                      </FormGroup>
                    </Col>

                    <Col lg="6">
                      <FormGroup>
                        <label className="form-control-label">Hours</label>
                        <Input
                          type="number"
                          value={hours}
                          onChange={(event) => setHours(event.target.value)}
                          max="8"
                          min="1"
                          required={true}
                          step={0.5}
                        />
                        {!isUpperManagement(currentUser) &&
                        !isAdmin(currentUser) &&
                        !isDirector(currentUser) &&
                        reason !== 'Day Off Non Paid' &&
                        !hollidayDates.includes(startDate) ? (
                          <>
                            {Number(hours) >
                              Number(selectedEmployeeData?.ptoPolicy?.currentBalance) -
                                Number(selectedEmployeeData?.ptoPolicy?.scheduleHours) && (
                              <label className="text-sm text-danger">
                                You cannot request more hours than your current limit of{' '}
                                {Number(selectedEmployeeData?.ptoPolicy?.currentBalance) -
                                  Number(selectedEmployeeData?.ptoPolicy?.scheduleHours) >
                                0
                                  ? Number(selectedEmployeeData?.ptoPolicy?.currentBalance) -
                                    Number(selectedEmployeeData?.ptoPolicy?.scheduleHours)
                                  : 0}
                                .{' Please change reason to "Day Off Non Paid".'}
                              </label>
                            )}
                          </>
                        ) : (
                          <></>
                        )}
                      </FormGroup>
                    </Col>

                    <Col lg="12">
                      <FormGroup>
                        <label className="form-control-label">Why are you taking off?</label>
                        <Input type="text" value={describe} onChange={(event) => setDescribe(event.target.value)} />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="d-flex align-items-center justify-content-end pr-3">
                    {policyName && (
                      <Button color="warning" disabled={disableSave}>
                        Save
                      </Button>
                    )}
                  </Row>
                </>
              )}
            </Form>
          </div>
        </>
      )}
    </Modal>
  );
};

export default ModalTimeoffRequest;
